import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Mail, Phone, Building2, MessageSquare, MapPin, Filter, Download, UserPlus, Flame, Snowflake, Sparkles, Copy, CheckCircle2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { formatDateTime } from "@/lib/format";

const STATUS_OPTIONS = [
  { id: "new", label: "Nuovo", tone: "bg-blue-50 text-blue-700 border-blue-200" },
  { id: "contacted", label: "Contattato", tone: "bg-amber-50 text-amber-800 border-amber-200" },
  { id: "qualified", label: "Qualificato", tone: "bg-purple-50 text-purple-700 border-purple-200" },
  { id: "converted", label: "Cliente", tone: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  { id: "archived", label: "Archiviato", tone: "bg-zinc-100 text-zinc-600 border-zinc-200" },
];

const SCORE_OPTIONS = [
  { id: "very_hot", label: "Very Hot", icon: Sparkles, tone: "bg-purple-100 text-purple-800 border-purple-300" },
  { id: "hot", label: "Hot", icon: Flame, tone: "bg-red-100 text-red-700 border-red-300" },
  { id: "warm", label: "Warm", icon: Flame, tone: "bg-amber-100 text-amber-800 border-amber-300" },
  { id: "cold", label: "Cold", icon: Snowflake, tone: "bg-sky-100 text-sky-800 border-sky-300" },
];

const STATUS_TONE = Object.fromEntries(STATUS_OPTIONS.map((s) => [s.id, s.tone]));
const STATUS_LABEL = Object.fromEntries(STATUS_OPTIONS.map((s) => [s.id, s.label]));
const SCORE_TONE = Object.fromEntries(SCORE_OPTIONS.map((s) => [s.id, s.tone]));
const SCORE_LABEL = Object.fromEntries(SCORE_OPTIONS.map((s) => [s.id, s.label]));
const SCORE_ICON = Object.fromEntries(SCORE_OPTIONS.map((s) => [s.id, s.icon]));

const PLAN_LABEL = {
  business_start: "Business Start",
  business_pro: "Business Pro",
  enterprise: "Enterprise",
  unsure: "Non so",
};
const CONTACT_LABEL = { email: "Email", phone: "Telefono", whatsapp: "WhatsApp" };
const FILTERS = [{ id: "all", label: "Tutti" }, ...STATUS_OPTIONS];

export default function AdminLeads() {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [scoreFilter, setScoreFilter] = useState("all");
  const [busy, setBusy] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const [convertResult, setConvertResult] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter !== "all") params.status = statusFilter;
      if (scoreFilter !== "all") params.score = scoreFilter;
      const { data } = await api.get("/admin/business-leads", { params });
      setLeads(data);
    } catch (e) {
      if (e.response?.status !== 403) toast.error(formatApiError(e.response?.data?.detail) || e.message);
      setLeads([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [statusFilter, scoreFilter]);

  const updateStatus = async (id, status) => {
    setBusy(id);
    try {
      await api.patch(`/admin/business-leads/${id}/status`, { status });
      toast.success("Stato aggiornato");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(null);
    }
  };

  const convertLead = async (id) => {
    if (!window.confirm("Vuoi creare un account business da questo lead? Verrà generata una password temporanea da comunicare al cliente.")) return;
    setBusy(id);
    try {
      const { data } = await api.post(`/admin/business-leads/${id}/convert`);
      setConvertResult(data);
      toast.success("Account business creato");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(null);
    }
  };

  const exportCsv = async () => {
    setBusy("csv");
    try {
      const params = new URLSearchParams();
      if (statusFilter !== "all") params.set("status", statusFilter);
      if (scoreFilter !== "all") params.set("score", scoreFilter);
      const qs = params.toString() ? `?${params.toString()}` : "";
      const res = await api.get(`/admin/business-leads.csv${qs}`, { responseType: "blob" });
      const blob = new Blob([res.data], { type: "text/csv;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const ts = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 19);
      a.href = url;
      a.download = `dapnow_leads_${ts}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 4000);
      toast.success("CSV esportato");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(null);
    }
  };

  // Status stats (independent from current filters)
  const [allCounts, setAllCounts] = useState({ status: {}, score: {}, total: 0 });
  useEffect(() => {
    api.get("/admin/business-leads").then(({ data }) => {
      const status = {};
      const score = {};
      data.forEach((l) => {
        status[l.status] = (status[l.status] || 0) + 1;
        score[l.lead_score_label] = (score[l.lead_score_label] || 0) + 1;
      });
      setAllCounts({ status, score, total: data.length });
    }).catch(() => {});
  }, [leads.length]);

  const totalAll = allCounts.total;

  return (
    <div className="space-y-5" data-testid="admin-leads-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="overline">Acquisizione</p>
          <h1 className="font-display text-3xl font-extrabold tracking-tighter mt-1">Lead Business</h1>
          <p className="text-sm text-dapnow-muted mt-1">Richieste demo dalla landing /business — i lead più caldi vengono mostrati per primi.</p>
        </div>
        <button
          type="button"
          onClick={exportCsv}
          disabled={busy === "csv" || leads.length === 0}
          data-testid="leads-export-csv-btn"
          className="btn-primary"
        >
          <Download className="h-4 w-4 mr-1.5" />
          {busy === "csv" ? "Esporto…" : `Esporta CSV (${leads.length})`}
        </button>
      </div>

      {/* Score stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {SCORE_OPTIONS.map((s) => {
          const Icon = s.icon;
          const active = scoreFilter === s.id;
          return (
            <button
              key={s.id}
              type="button"
              onClick={() => setScoreFilter(active ? "all" : s.id)}
              data-testid={`leads-score-stat-${s.id}`}
              className={`text-left rounded-2xl p-4 border-2 transition ${active ? `${s.tone} shadow-sm` : "border-dapnow-line bg-white hover:border-dapnow-blue/40"}`}
            >
              <div className="flex items-center justify-between">
                <p className="overline text-[10px]">{s.label}</p>
                <Icon className={`h-4 w-4 ${active ? "" : "text-dapnow-muted"}`} />
              </div>
              <p className="font-display text-2xl font-extrabold tracking-tighter mt-1">{allCounts.score?.[s.id] || 0}</p>
            </button>
          );
        })}
      </div>

      {/* Status stats */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        {STATUS_OPTIONS.map((s) => (
          <button
            key={s.id}
            type="button"
            onClick={() => setStatusFilter(statusFilter === s.id ? "all" : s.id)}
            data-testid={`leads-stat-${s.id}`}
            className={`text-left rounded-2xl p-3 border transition ${statusFilter === s.id ? "border-dapnow-blue bg-dapnow-blue-soft" : "border-dapnow-line bg-white hover:border-dapnow-blue/40"}`}
          >
            <p className="overline text-[10px]">{s.label}</p>
            <p className="font-display text-xl font-extrabold tracking-tighter mt-1">{allCounts.status?.[s.id] || 0}</p>
          </button>
        ))}
      </div>

      {/* Filter pills */}
      <div className="flex flex-wrap gap-2 items-center">
        <Filter className="h-4 w-4 text-dapnow-muted shrink-0" />
        {FILTERS.map((f) => (
          <button
            key={f.id}
            type="button"
            onClick={() => setStatusFilter(f.id)}
            data-testid={`leads-filter-${f.id}`}
            className={`shrink-0 rounded-full px-4 h-9 text-xs font-semibold transition ${
              statusFilter === f.id ? "bg-dapnow-ink text-white" : "bg-white border border-dapnow-line text-dapnow-ink hover:border-dapnow-blue/40"
            }`}
          >
            {f.label}{f.id === "all" ? ` (${totalAll})` : ""}
          </button>
        ))}
        {scoreFilter !== "all" && (
          <button
            type="button"
            onClick={() => setScoreFilter("all")}
            data-testid="leads-clear-score-filter"
            className="ml-auto shrink-0 rounded-full px-3 h-9 text-xs font-semibold bg-white border border-dapnow-line hover:border-dapnow-red/40 text-dapnow-red"
          >
            Rimuovi filtro {SCORE_LABEL[scoreFilter]}
          </button>
        )}
      </div>

      {loading ? (
        <div className="card-soft p-10 text-center text-dapnow-muted">Caricamento…</div>
      ) : leads.length === 0 ? (
        <div className="card-soft p-10 text-center">
          <Building2 className="h-6 w-6 mx-auto text-dapnow-blue" />
          <p className="mt-3 font-display text-lg font-semibold tracking-tight">Nessuna richiesta</p>
          <p className="text-sm text-dapnow-muted mt-1">Le richieste demo dal form B2B compariranno qui.</p>
        </div>
      ) : (
        <div className="grid lg:grid-cols-2 gap-4">
          {leads.map((l) => {
            const isOpen = expanded === l.id;
            const ScoreIcon = SCORE_ICON[l.lead_score_label] || Snowflake;
            return (
              <div key={l.id} className="card-soft p-5 space-y-4 relative" data-testid={`lead-card-${l.id}`}>
                <span
                  data-testid={`lead-score-${l.id}`}
                  className={`absolute -top-2 left-4 inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-[11px] font-bold uppercase tracking-wider ${SCORE_TONE[l.lead_score_label] || ""}`}
                >
                  <ScoreIcon className="h-3 w-3" />
                  {(SCORE_LABEL[l.lead_score_label] || l.lead_score_label).toUpperCase()} · {l.lead_score}
                </span>

                <div className="flex items-start justify-between gap-3 pt-1">
                  <div className="min-w-0">
                    <p className="overline">#{l.id.slice(0, 8).toUpperCase()}</p>
                    <h3 className="font-display text-xl font-bold tracking-tight mt-0.5 truncate">{l.company_name}</h3>
                    <p className="text-sm text-dapnow-muted truncate">{l.contact_name} · {l.business_sector}</p>
                  </div>
                  <span className={`pill border ${STATUS_TONE[l.status] || ""} text-[11px] font-semibold whitespace-nowrap`}>
                    {STATUS_LABEL[l.status] || l.status}
                  </span>
                </div>

                <div className="grid sm:grid-cols-2 gap-3 text-sm">
                  <a href={`mailto:${l.email}`} className="flex items-center gap-2 text-dapnow-ink hover:text-dapnow-blue truncate">
                    <Mail className="h-4 w-4 shrink-0 text-dapnow-muted" />
                    <span className="truncate">{l.email}</span>
                  </a>
                  <a href={`tel:${l.phone}`} className="flex items-center gap-2 text-dapnow-ink hover:text-dapnow-blue truncate">
                    <Phone className="h-4 w-4 shrink-0 text-dapnow-muted" />
                    <span className="truncate">{l.phone}</span>
                  </a>
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
                  <Mini label="Consegne/mese" value={l.monthly_deliveries_estimate} />
                  <Mini label="Piano interesse" value={l.interested_plan ? PLAN_LABEL[l.interested_plan] : "—"} />
                  <Mini label="Contatto pref." value={l.preferred_contact_method ? CONTACT_LABEL[l.preferred_contact_method] : "—"} />
                  <Mini label="Zona" value={l.operating_area || "—"} icon={MapPin} />
                </div>

                {isOpen && (
                  <div className="rounded-2xl bg-dapnow-bg p-4">
                    <p className="overline mb-2 flex items-center gap-1.5"><MessageSquare className="h-3 w-3" /> Messaggio</p>
                    <p className="text-sm whitespace-pre-wrap">{l.message}</p>
                  </div>
                )}

                <div className="flex items-center justify-between pt-1 border-t border-dapnow-line">
                  <p className="text-[11px] text-dapnow-muted">{formatDateTime(l.created_at)}</p>
                  <button
                    type="button"
                    onClick={() => setExpanded(isOpen ? null : l.id)}
                    data-testid={`lead-toggle-${l.id}`}
                    className="text-xs font-semibold text-dapnow-blue hover:underline"
                  >
                    {isOpen ? "Nascondi messaggio" : "Vedi messaggio"}
                  </button>
                </div>

                <div className="flex flex-wrap gap-2 pt-1">
                  {STATUS_OPTIONS.map((s) => (
                    <button
                      key={s.id}
                      type="button"
                      onClick={() => updateStatus(l.id, s.id)}
                      disabled={busy === l.id || l.status === s.id}
                      data-testid={`lead-set-${l.id}-${s.id}`}
                      className={`rounded-full px-3 h-8 text-[11px] font-semibold border transition ${
                        l.status === s.id ? `${STATUS_TONE[s.id]} cursor-default` : "bg-white border-dapnow-line text-dapnow-ink hover:border-dapnow-blue/40"
                      }`}
                    >
                      → {s.label}
                    </button>
                  ))}
                </div>

                {l.status !== "converted" && !l.converted_user_id && (
                  <button
                    type="button"
                    onClick={() => convertLead(l.id)}
                    disabled={busy === l.id}
                    data-testid={`lead-convert-${l.id}`}
                    className="btn-primary w-full text-xs h-10"
                  >
                    <UserPlus className="h-3.5 w-3.5 mr-1.5" />
                    {busy === l.id ? "Creazione…" : "Crea account business da questo lead"}
                  </button>
                )}
                {l.converted_user_id && (
                  <p className="text-xs text-emerald-700 font-semibold flex items-center gap-1.5 bg-emerald-50 rounded-xl p-2.5 border border-emerald-200" data-testid={`lead-converted-${l.id}`}>
                    <CheckCircle2 className="h-3.5 w-3.5" /> Account business creato — user_id: <code className="text-[10px] font-mono">{l.converted_user_id.slice(0, 8)}</code>
                  </p>
                )}
              </div>
            );
          })}
        </div>
      )}

      {convertResult && (
        <ConvertSuccessModal data={convertResult} onClose={() => setConvertResult(null)} />
      )}
    </div>
  );
}

function ConvertSuccessModal({ data, onClose }) {
  const [copied, setCopied] = useState(false);
  const copyAll = async () => {
    const text = `DapNow — Account business creato\nEmail: ${data.email}\nPassword temporanea: ${data.temp_password}\nLink login: ${window.location.origin}/login`;
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Impossibile copiare");
    }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="bg-white rounded-3xl p-6 sm:p-8 max-w-md w-full" data-testid="convert-success-modal">
        <span className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-emerald-100 text-emerald-700">
          <CheckCircle2 className="h-6 w-6" />
        </span>
        <h2 className="font-display text-2xl font-extrabold tracking-tighter mt-4">Account business creato</h2>
        <p className="text-sm text-dapnow-muted mt-2">Comunica queste credenziali al referente del lead. L'utente è in stato <strong>{data.activation_status}</strong>.</p>

        <div className="mt-5 space-y-3 rounded-2xl bg-dapnow-bg p-4 text-sm">
          <KV k="Email" v={data.email} />
          <KV k="Password temp." v={data.temp_password} mono />
          <KV k="User ID" v={data.user_id.slice(0, 12) + "…"} mono />
        </div>

        <div className="mt-5 flex gap-2">
          <button onClick={copyAll} data-testid="convert-copy-btn" className="btn-ghost flex-1">
            <Copy className="h-3.5 w-3.5 mr-1.5" /> {copied ? "Copiato!" : "Copia tutto"}
          </button>
          <button onClick={onClose} data-testid="convert-close-btn" className="btn-primary flex-1">Chiudi</button>
        </div>
      </div>
    </div>
  );
}

function KV({ k, v, mono }) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <span className="text-xs uppercase tracking-wider text-dapnow-muted font-semibold">{k}</span>
      <span className={`text-sm font-semibold text-dapnow-ink truncate ${mono ? "font-mono" : ""}`}>{v}</span>
    </div>
  );
}

function Mini({ label, value, icon: Icon }) {
  return (
    <div className="rounded-xl bg-dapnow-bg p-2.5">
      <p className="overline text-[9px] flex items-center gap-1">
        {Icon && <Icon className="h-2.5 w-2.5" />} {label}
      </p>
      <p className="font-display text-sm font-semibold mt-0.5 truncate">{value}</p>
    </div>
  );
}
